import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../hooks/redux'
import { updateUser, logout } from '../store/slices/authSlice'
import { changePortalPassword, signOutPortalUser, updatePortalProfile } from '../services/guestPortalSupabase'

export default function GuestSettingsPage() {
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)

  // ── Profile form state ──────────────────────────────────────────────
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
  })
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const navigate = useNavigate()

  // ── Delete profile state ────────────────────────────────────────────
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteMsg, setDeleteMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // ── Password form state ─────────────────────────────────────────────
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const flash = (
    set: typeof setProfileMsg,
    type: 'success' | 'error',
    text: string
  ) => {
    set({ type, text })
    setTimeout(() => set(null), 5000)
  }

  // ── Save profile ────────────────────────────────────────────────────
  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    const { firstName, lastName } = profileForm
    if (!firstName.trim() || !lastName.trim()) {
      return flash(setProfileMsg, 'error', 'First name and last name are required.')
    }
    setProfileLoading(true)

    try {
      const updatedUser = await updatePortalProfile(firstName.trim(), lastName.trim())
      dispatch(updateUser({ firstName: updatedUser.firstName, lastName: updatedUser.lastName }))
      flash(setProfileMsg, 'success', 'Profile updated successfully.')
    } catch (error) {
      flash(setProfileMsg, 'error', error instanceof Error ? error.message : 'Failed to update profile.')
    } finally {
      setProfileLoading(false)
    }
  }

  // ── Change password ─────────────────────────────────────────────────
  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    const { currentPassword, newPassword, confirmPassword } = passwordForm

    if (!currentPassword || !newPassword || !confirmPassword) {
      return flash(setPasswordMsg, 'error', 'All password fields are required.')
    }
    if (newPassword.length < 8) {
      return flash(setPasswordMsg, 'error', 'New password must be at least 8 characters.')
    }
    if (newPassword !== confirmPassword) {
      return flash(setPasswordMsg, 'error', 'New passwords do not match.')
    }

    setPasswordLoading(true)
    try {
      if (!user?.email) {
        throw new Error('Your session has expired. Please sign in again.')
      }

      await changePortalPassword(user.email, currentPassword, newPassword)
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
      flash(setPasswordMsg, 'success', 'Password changed successfully.')
    } catch (error) {
      flash(setPasswordMsg, 'error', error instanceof Error ? error.message : 'Network error — please try again.')
    } finally {
      setPasswordLoading(false)
    }
  }

  // ── Delete profile ─────────────────────────────────────────────────
  const deleteProfile = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteMsg({ type: 'error', text: 'Please type DELETE to confirm.' })
      return
    }
    setDeleteLoading(true)
    setDeleteMsg({
      type: 'error',
      text: 'Profile deletion still requires the legacy admin path. Contact Marina Glen to remove your account safely.',
    })
    setDeleteLoading(false)
  }

  const signOutAndLeave = async () => {
    setDeleteLoading(true)
    await signOutPortalUser().catch(() => undefined)
    localStorage.clear()
    dispatch(logout())
    setDeleteLoading(false)
    navigate('/')
  }

  // ── UI helpers ──────────────────────────────────────────────────────
  const inputCls =
    'w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 text-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100 transition-colors bg-white'

  const MessageBanner = ({
    msg,
  }: {
    msg: { type: 'success' | 'error'; text: string } | null
  }) =>
    msg ? (
      <div
        className={`rounded-xl px-5 py-3 text-sm font-medium ${
          msg.type === 'success'
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}
      >
        {msg.text}
      </div>
    ) : null

  return (
    <div className="space-y-8 max-w-2xl mx-auto">
      {/* Page header */}
      <section className="rounded-3xl border border-sky-100 bg-white/95 p-8 shadow-lg backdrop-blur">
        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.22em] text-sky-600">Account Settings</p>
        <h2 className="text-3xl font-bold text-slate-900">Your Profile</h2>
        <p className="mt-2 text-slate-600">Update your personal details and change your password.</p>
      </section>

      {/* Profile details */}
      <section className="rounded-3xl border border-slate-200 bg-white/95 p-8 shadow-lg backdrop-blur">
        <h3 className="text-xl font-bold text-slate-900 mb-6">Personal Information</h3>

        <form onSubmit={saveProfile} className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">First Name</label>
              <input
                type="text"
                className={inputCls}
                value={profileForm.firstName}
                onChange={(e) => setProfileForm((f) => ({ ...f, firstName: e.target.value }))}
                placeholder="First name"
                autoComplete="given-name"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Last Name</label>
              <input
                type="text"
                className={inputCls}
                value={profileForm.lastName}
                onChange={(e) => setProfileForm((f) => ({ ...f, lastName: e.target.value }))}
                placeholder="Last name"
                autoComplete="family-name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address</label>
            <input
              type="email"
              className={`${inputCls} bg-slate-50 text-slate-500 cursor-not-allowed`}
              value={user?.email || ''}
              readOnly
              title="Email address cannot be changed"
            />
            <p className="mt-1 text-xs text-slate-400">Email cannot be changed. Contact support if needed.</p>
          </div>

          <MessageBanner msg={profileMsg} />

          <button
            type="submit"
            disabled={profileLoading}
            className="rounded-xl bg-sky-600 px-7 py-3 text-sm font-bold text-white hover:bg-sky-700 disabled:opacity-60 transition-colors"
          >
            {profileLoading ? 'Saving…' : 'Save Profile'}
          </button>
        </form>
      </section>

      {/* Change password */}
      <section className="rounded-3xl border border-slate-200 bg-white/95 p-8 shadow-lg backdrop-blur">
        <h3 className="text-xl font-bold text-slate-900 mb-2">Change Password</h3>
        <p className="text-sm text-slate-500 mb-6">Choose a strong password of at least 8 characters.</p>

        <form onSubmit={changePassword} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Current Password</label>
            <input
              type="password"
              className={inputCls}
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm((f) => ({ ...f, currentPassword: e.target.value }))}
              placeholder="Enter current password"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">New Password</label>
            <input
              type="password"
              className={inputCls}
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm((f) => ({ ...f, newPassword: e.target.value }))}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Confirm New Password</label>
            <input
              type="password"
              className={inputCls}
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm((f) => ({ ...f, confirmPassword: e.target.value }))}
              placeholder="Repeat new password"
              autoComplete="new-password"
            />
          </div>

          <MessageBanner msg={passwordMsg} />

          <button
            type="submit"
            disabled={passwordLoading}
            className="rounded-xl bg-slate-900 px-7 py-3 text-sm font-bold text-white hover:bg-slate-700 disabled:opacity-60 transition-colors"
          >
            {passwordLoading ? 'Updating…' : 'Change Password'}
          </button>
        </form>
      </section>
      {/* Delete profile – danger zone */}
      <section className="rounded-3xl border border-red-200 bg-white/95 p-8 shadow-lg backdrop-blur">
        <h3 className="text-xl font-bold text-red-700 mb-2">Danger Zone</h3>
        <p className="text-sm text-slate-500 mb-6">
          Guest account deletion still requires a manual admin workflow because it affects operational booking records. Use the request action below if you want your profile removed.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Type <span className="font-mono text-red-600">DELETE</span> to confirm
            </label>
            <input
              type="text"
              className={`${inputCls} border-red-300 focus:border-red-400 focus:ring-red-100`}
              value={deleteConfirmText}
              onChange={(e) => {
                setDeleteConfirmText(e.target.value)
                setDeleteMsg(null)
              }}
              placeholder="DELETE"
            />
          </div>

          <MessageBanner msg={deleteMsg} />

          <button
            type="button"
            onClick={deleteProfile}
            disabled={deleteLoading}
            className="rounded-xl bg-red-600 px-7 py-3 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60 transition-colors"
          >
            {deleteLoading ? 'Sending…' : 'Request Profile Deletion'}
          </button>
          <button
            type="button"
            onClick={signOutAndLeave}
            disabled={deleteLoading}
            className="rounded-xl border border-slate-300 px-7 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors"
          >
            Sign Out Instead
          </button>
        </div>
      </section>
    </div>
  )
}
